import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    TopBar,
    BulkAddTagsModal,
    AssignMoveGroupModal,
    UploadMultipleSourcesModal,
    CsvMigrationsUploadModal,
    PreflightMonitor,
    RefreshSourcesModal,
} from '../../components';
import {
    SOURCE_INVENTORY_PRIMARY_BUTTONS_CONFIG,
    START_MIGRATION_PRIMARY_BUTTONS_CONFIG,
    SECONDARY_BUTTONS_CONFIG,
    SOURCE_RESOURCE_NOUNS,
} from './actions';
import SourcesTable from './SourcesTable';
import { useCsvMigrationPreflight, useRefreshSources } from '../../hooks';
import { ROUTES, ROUTE_CONFIG } from '../../constants/routes';
import { DOC_TYPES } from '../../constants/docLinks';
import { filterGroupRowKeys } from '../../lib/helpers';
import './Sources.scss';

// "Manage Sources" and "Start Migration" are two identities of this page,
// each with its own route
export const Sources = React.memo(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const isStartMigrationMode = location.pathname === ROUTES.startMigrations;
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [addSourceModalOpen, setAddSourceModalOpen] = useState(false);
    const [uploadSourcesModalOpen, setUploadSourcesModalOpen] = useState(false);
    const [csvMigrationModalOpen, setCsvMigrationModalOpen] = useState(false);
    const [csvPreflightModalOpen, setCsvPreflightModalOpen] = useState(false);
    const [refreshSourcesModalOpen, setRefreshSourcesModalOpen] = useState(false);
    const [addTagsModalOpen, setAddTagsModalOpen] = useState(false);
    const [assignMoveGroupModalOpen, setAssignMoveGroupModalOpen] = useState(false);
    const [selectedSourcesForTags, setSelectedSourcesForTags] = useState([]);
    const [selectedSourcesForMoveGroup, setSelectedSourcesForMoveGroup] = useState([]);
    const bulkDeleteTriggerRef = useRef(null);
    const bulkConfigureMigrationTriggerRef = useRef(null);
    const bulkConfigureInSituTriggerRef = useRef(null);
    const preflightTriggerRef = useRef(null); // Store preflight trigger from SourcesTable
    const refetchDataRef = useRef(null); // Store refetch function from SourcesTable
    const searchDebounceRef = useRef(null);

    const userOrg = sessionStorage.getItem('userOrg');

    // Filter out group keys to get only actual source IDs
    const selectedSourceIds = useMemo(() => filterGroupRowKeys(selectedRowKeys), [selectedRowKeys]);
    const selectedCount = selectedSourceIds.length;

    // CSV Migration Preflight hook
    const {
        migrationProfile,
        preflightPayload,
        isLoadingProfile,
        isProcessing,
        startPreflight,
        handlePreflightComplete,
        reset: resetCsvPreflight,
        migrationContext,
    } = useCsvMigrationPreflight();

    // Refresh Sources hook
    const { startSourceRefresh, stopPolling } = useRefreshSources({
        userOrg,
        onSuccess: (newSourceIds) => {
            // Refresh the sources table
            if (refetchDataRef.current) {
                refetchDataRef.current();
            }
        },
        onError: (error) => {
            console.error('Error refreshing sources:', error);
        },
    });

    const handleSearch = useCallback((query) => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        setSearchQuery(query);
    }, []);

    const handleSearchInputChange = useCallback((e) => {
        const value = e.target.value;
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            setSearchQuery(value);
        }, 500);
    }, []);

    const handleBulkDelete = useCallback(() => {
        if (selectedCount > 0 && bulkDeleteTriggerRef.current) {
            bulkDeleteTriggerRef.current(selectedSourceIds);
        }
    }, [selectedSourceIds, selectedCount]);

    const handleBulkConfigureMigration = useCallback(() => {
        if (selectedCount > 0 && bulkConfigureMigrationTriggerRef.current) {
            bulkConfigureMigrationTriggerRef.current(selectedSourceIds);
        }
    }, [selectedSourceIds, selectedCount]);

    const handleBulkConfigureInSitu = useCallback(() => {
        if (selectedCount > 0 && bulkConfigureInSituTriggerRef.current) {
            bulkConfigureInSituTriggerRef.current(selectedSourceIds);
        }
    }, [selectedSourceIds, selectedCount]);

    const handleBulkAddTags = useCallback(() => {
        if (selectedCount > 0) {
            setSelectedSourcesForTags(selectedSourceIds);
            setAddTagsModalOpen(true);
        }
    }, [selectedSourceIds, selectedCount]);

    const handleBulkAssignMoveGroup = useCallback(() => {
        if (selectedCount > 0) {
            setSelectedSourcesForMoveGroup(selectedSourceIds);
            setAssignMoveGroupModalOpen(true);
        }
    }, [selectedSourceIds, selectedCount]);

    const handleAddSource = useCallback(() => {
        setAddSourceModalOpen(true);
    }, []);

    const handleImportFromDiscovery = useCallback(() => {
        navigate('/discovery/inventory');
    }, [navigate]);

    const handleAddSourceModalClose = useCallback(() => {
        setAddSourceModalOpen(false);
    }, []);

    const handleUploadSources = useCallback(() => {
        setUploadSourcesModalOpen(true);
    }, []);

    const handleUploadSourcesModalClose = useCallback(() => {
        setUploadSourcesModalOpen(false);
    }, []);

    const handleRunCsvMigration = useCallback(() => {
        setCsvMigrationModalOpen(true);
    }, []);

    const handleCsvMigrationModalClose = useCallback(() => {
        setCsvMigrationModalOpen(false);
    }, []);

    const handleCsvMigrationSuccess = useCallback(
        (result) => {
            // Refresh sources table
            if (refetchDataRef.current) {
                refetchDataRef.current();
            }

            // Close CSV modal
            setCsvMigrationModalOpen(false);

            // Start React preflight flow instead of backbone
            if (result && result.migrationProfileID) {
                // Use setTimeout to match backbone timing
                setTimeout(() => {
                    startPreflight(result.migrationProfileID);
                }, 200);
            }
        },
        [startPreflight],
    );

    const handleUploadSourcesSuccess = useCallback((continuePayload) => {
        // Refresh sources table
        if (refetchDataRef.current) {
            refetchDataRef.current();
        }

        // If we have a preflight payload, trigger the preflight check directly
        if (continuePayload && continuePayload.preflightPayload && preflightTriggerRef.current) {
            const migrationContext = {
                applianceId: continuePayload.applianceID,
                targetAccount: continuePayload.targetCloudID,
                cloudAccount: {
                    id: continuePayload.targetCloudID,
                    cloud_type: continuePayload.cloudType,
                    name: 'Selected Target Cloud',
                    appliance: { id: continuePayload.applianceID },
                },
                downloadCSV: continuePayload.downloadCSV,
                csvDownloadPayload: continuePayload.downloadCSV
                    ? {
                          cloud_account: continuePayload.targetCloudID,
                          cloud_type: continuePayload.cloudType,
                          appliance_id: continuePayload.applianceID,
                          sources: continuePayload.sourceConfigIDs,
                          migration_method: 'os-based',
                      }
                    : null,
            };

            // Trigger preflight modal directly
            preflightTriggerRef.current(continuePayload.preflightPayload, migrationContext);
        }
    }, []);

    const handleAddTagsModalClose = useCallback(() => {
        setAddTagsModalOpen(false);
        setSelectedSourcesForTags([]);
    }, []);

    const handleAssignMoveGroupModalClose = useCallback(() => {
        setAssignMoveGroupModalOpen(false);
        setSelectedSourcesForMoveGroup([]);
    }, []);

    const handleBulkActionSuccess = useCallback(() => {
        // Clear selected rows after successful bulk action
        setSelectedRowKeys([]);
        // Refresh table data to show updated tags/move groups
        if (refetchDataRef.current) {
            refetchDataRef.current();
        }
    }, []);

    const handleDataRefreshNeeded = useCallback((refetchFunction) => {
        refetchDataRef.current = refetchFunction;
    }, []);

    // Effect to open preflight modal when payload is ready
    useEffect(() => {
        if (preflightPayload && migrationProfile && !csvPreflightModalOpen) {
            setCsvPreflightModalOpen(true);
        }
    }, [preflightPayload, migrationProfile, csvPreflightModalOpen]);

    // Handle CSV preflight completion
    const handleCsvPreflightComplete = useCallback(
        (result) => {
            setCsvPreflightModalOpen(false);
            if (result && result.success) {
                // Extract selected source configs from preflight results
                const selectedSourceConfigs = result.preflights?.filter((p) => p.type === 'source_config') || [];
                handlePreflightComplete(selectedSourceConfigs);
            }
            resetCsvPreflight();
        },
        [handlePreflightComplete, resetCsvPreflight],
    );

    // Handle CSV preflight cancellation
    const handleCsvPreflightCancel = useCallback(() => {
        setCsvPreflightModalOpen(false);
        resetCsvPreflight();
    }, [resetCsvPreflight]);

    const handleRefreshSources = useCallback(() => {
        setRefreshSourcesModalOpen(true);
    }, []);

    const handleRefreshSourcesModalClose = useCallback(() => {
        setRefreshSourcesModalOpen(false);
    }, []);

    const handleRefreshSourcesSuccess = useCallback(
        async (refreshParams) => {
            setRefreshSourcesModalOpen(false);

            // Get cloud account data for CSV download
            const cloudAccountData = {
                id: refreshParams.cloudAccountId,
                type: 'vsphere', // or get from the actual cloud account
                appliance: {
                    id: refreshParams.applianceId,
                },
            };
            // Start refresh with CSV download flag if requested
            // The useRefreshSources hook will automatically trigger CSV download after polling completes
            await startSourceRefresh(
                { ...refreshParams, shouldDownloadCSV: refreshParams.downloadCSV },
                cloudAccountData,
            );
        },
        [startSourceRefresh],
    );

    useEffect(() => {
        return () => {
            // Stop source refresh polling on unmount
            if (stopPolling) {
                stopPolling();
            }
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, [stopPolling]);

    // Actions for the current identity's primary buttons, keyed to match
    // SOURCE_INVENTORY_PRIMARY_BUTTONS_CONFIG / START_MIGRATION_PRIMARY_BUTTONS_CONFIG.
    const sourceInventoryActions = useMemo(
        () => ({
            addSource: handleAddSource,
            csvSources: handleUploadSources,
            importFromDiscovery: handleImportFromDiscovery,
            vmBasedImport: handleRefreshSources,
        }),
        [handleAddSource, handleUploadSources, handleImportFromDiscovery, handleRefreshSources],
    );

    const startMigrationActions = useMemo(
        () => ({
            inspectAndMigrate: handleBulkConfigureMigration,
            runCsvMigration: handleRunCsvMigration,
            modernizeInPlace: handleBulkConfigureInSitu,
        }),
        [handleBulkConfigureMigration, handleRunCsvMigration, handleBulkConfigureInSitu],
    );

    // Secondary (bulk selection) button actions - shared by both identities.
    const secondaryButtonActions = useMemo(
        () => ({
            delete: handleBulkDelete,
            addTags: handleBulkAddTags,
            assignMoveGroup: handleBulkAssignMoveGroup,
        }),
        [handleBulkDelete, handleBulkAddTags, handleBulkAssignMoveGroup],
    );

    const primaryButtonsConfig = isStartMigrationMode
        ? START_MIGRATION_PRIMARY_BUTTONS_CONFIG
        : SOURCE_INVENTORY_PRIMARY_BUTTONS_CONFIG;

    const actions = useMemo(
        () => ({
            ...(isStartMigrationMode ? startMigrationActions : sourceInventoryActions),
            ...secondaryButtonActions,
        }),
        [isStartMigrationMode, startMigrationActions, sourceInventoryActions, secondaryButtonActions],
    );

    return (
        <div className={'sources-page'}>
            <TopBar
                breadcrumbItems={
                    isStartMigrationMode
                        ? [{ title: 'Migrate' }, { title: ROUTE_CONFIG.startMigrations.title }]
                        : [{ title: 'Manage' }, { title: ROUTE_CONFIG.sources.title }]
                }
                tabsMode="route"
                tabs={[
                    { title: 'Manage Sources', route: ROUTES.sources },
                    { title: 'Start Migration', route: ROUTES.startMigrations },
                ]}
                primaryButtons={primaryButtonsConfig}
                secondaryButtons={SECONDARY_BUTTONS_CONFIG}
                actions={actions}
                selectedCount={selectedCount}
                resourceNouns={SOURCE_RESOURCE_NOUNS}
                documentationType={DOC_TYPES.MIGRATION}
                search={{
                    className: 'sources-table-search-input',
                    placeholder: 'Source IP, Source Name, Tag, or Move Group Name',
                    onSearch: handleSearch,
                    onChange: handleSearchInputChange,
                }}
            />
            <SourcesTable
                combinedSearch={searchQuery}
                selectedRowKeys={selectedRowKeys}
                onSelectedRowKeysChange={setSelectedRowKeys}
                bulkDeleteTriggerRef={bulkDeleteTriggerRef}
                bulkConfigureMigrationTriggerRef={bulkConfigureMigrationTriggerRef}
                addSourceModalOpen={addSourceModalOpen}
                onAddSourceModalClose={handleAddSourceModalClose}
                bulkConfigureInSituTriggerRef={bulkConfigureInSituTriggerRef}
                preflightTriggerRef={preflightTriggerRef}
                onDataRefreshNeeded={handleDataRefreshNeeded}
            />

            <BulkAddTagsModal
                open={addTagsModalOpen}
                onClose={handleAddTagsModalClose}
                selectedResources={selectedSourcesForTags}
                userOrg={userOrg}
                onSuccess={handleBulkActionSuccess}
                title="Add Tags to Selected Source(s)"
                resourceType="source"
                includeMoveGroupInfo={true}
                updateTagsByQueryParameter={true}
                apiConfig={{
                    getResourceEndpoint: (resourceId) => `/api/v3/sources/${resourceId}`,
                    updateResourceEndpoint: (resourceId) => `/api/v3/sources/${resourceId}`,
                    updateMethod: 'PATCH',
                }}
            />

            <AssignMoveGroupModal
                open={assignMoveGroupModalOpen}
                onClose={handleAssignMoveGroupModalClose}
                selectedSources={selectedSourcesForMoveGroup}
                userOrg={userOrg}
                onSuccess={handleBulkActionSuccess}
            />

            <UploadMultipleSourcesModal
                open={uploadSourcesModalOpen}
                onClose={handleUploadSourcesModalClose}
                onSuccess={handleUploadSourcesSuccess}
                userOrg={userOrg}
            />

            <CsvMigrationsUploadModal
                open={csvMigrationModalOpen}
                onClose={handleCsvMigrationModalClose}
                onSuccess={handleCsvMigrationSuccess}
                userOrg={userOrg}
            />

            <RefreshSourcesModal
                open={refreshSourcesModalOpen}
                onClose={handleRefreshSourcesModalClose}
                onSuccess={handleRefreshSourcesSuccess}
                userOrg={userOrg}
            />

            {/* CSV Migration Preflight Monitor */}
            <PreflightMonitor
                open={csvPreflightModalOpen}
                onClose={handleCsvPreflightCancel}
                onComplete={handleCsvPreflightComplete}
                preflightRequests={preflightPayload || []}
                title="Migrations Preflight"
                loadingMessage="Initiating Migrations Preflight..."
                proceedOnSuccess={true}
                migrationContext={migrationContext}
                inspectSourcesMode={false}
            />
        </div>
    );
});

Sources.displayName = 'Sources';
